import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import * as v from 'valibot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { Button, Chip } from '@v2/components';
import { styles } from './styles';
import type { PimsConfigRowProps } from './types';

const configValueSchema = v.object({
  value: v.string(),
});

type ConfigValueForm = v.InferOutput<typeof configValueSchema>;

const formatDisplayValue = (value: string): string =>
  value.replace(/;/g, '; ').replace(/\|\|\|\|/g, ' |||| ');

const PimsConfigRow: React.FC<PimsConfigRowProps> = ({ config, onSave, isSaving }) => {
  const [isEditing, setIsEditing] = useState(false);
  const useTextarea = (config.value?.length ?? 0) > 12;

  const { register, handleSubmit, reset } = useForm<ConfigValueForm>({
    resolver: valibotResolver(configValueSchema),
    defaultValues: { value: config.value ?? '' },
  });

  const handleCancel = (): void => {
    reset({ value: config.value ?? '' });
    setIsEditing(false);
  };

  const handleFormSubmit = (data: ConfigValueForm): void => {
    onSave(config, data.value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className={styles.row}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.cellContainer}>
          <div className={styles.keyCell}>{config.key}</div>
          <div className={styles.editContainer}>
            {useTextarea ? (
              <textarea className={styles.textarea} {...register('value')} />
            ) : (
              <input className={styles.input} type="text" {...register('value')} />
            )}
            <div className={styles.editActions}>
              <Button
                type="submit"
                variant="ghost-success"
                size="sm"
                disabled={isSaving}
                isLoading={isSaving}>
                Save
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  const isNull = config.value == null || config.value === '';

  return (
    <div className={styles.row}>
      <div className={styles.cellContainer}>
        <div className={styles.keyCell}>{config.key}</div>
        <div className={styles.valueCell}>
          {isNull ? (
            <Chip size="sm" className={styles.nullChip}>
              NULL
            </Chip>
          ) : (
            formatDisplayValue(config.value)
          )}
        </div>
        <div className={styles.actionsCell}>
          <Button
            variant="ghost-primary"
            size="sm"
            onClick={() => setIsEditing(true)}
            icon={<FontAwesomeIcon icon={faPen} />}
          />
        </div>
      </div>
    </div>
  );
};

export { PimsConfigRow };
export default PimsConfigRow;
